import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import AutomatedReplyProvider from "./AutomatedReplyProvider";
import ShowToast from "../../../widget/common/ui/Toast";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import { AutomatedReplyModel } from "../../../model/work_profile/AutomatedReplyModel";
import { textCase } from "../../../service/ui/UI";
import Loader from "../../../widget/common/ui/Loader";
import { AlertConfirm, AlertMesage } from "../../../widget/common/ui/Alerts";
import { useDispatch } from "react-redux";
import {
  addModal,
  removeAllModal,
} from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";

type AutomatedReplyTypes = {
  maxWords: number;
  statusField: boolean;
  messageField: string;
  loadedStatus: boolean;
  allowEdit: boolean;

  handleEdit: () => void;
  handleSubmit: () => void;
  handleClear: () => void;
  handleOnChange: (
    name: string,
    value: string,
    isValid?: boolean | undefined
  ) => void;
};

const AutomatedReplyContext = createContext<AutomatedReplyTypes | undefined>(
  undefined
);

export const AutomatedReplyContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const maxWords = 2000;

  const dispatch = useDispatch();
  const messageValid = useRef<boolean>(false);
  const messageCurrentRef = useRef<string>("");

  const [statusField, setStatusField] = useState<boolean>(false);
  const [loadedStatus, setLoadedStatus] = useState<boolean>(false);
  const [messageField, setMessageField] = useState<string>("");

  const [allowEdit, setAllowEdit] = useState<boolean>(true);

  const resetForm = () => {
    messageValid.current = false;
    messageCurrentRef.current = "";
    setStatusField(false);
    setLoadedStatus(false);
    setMessageField("");
    setAllowEdit(true);
  };

  const handleOnChange = (
    fieldName: string,
    fieldValue: string,
    isValid?: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();

    if (fieldName === "toggle-activate") {
      if (statusField && UtilityHelper.isNotEmpty(messageCurrentRef.current)) {
        AlertConfirm({
          title: "automated_reply_deactivated_confirm",
          onConfirm: async () => {
            setStatusField(false);
            Loader.modal();
            const statusResult =
              await AutomatedReplyProvider.updateAutomatedAPI({
                message: messageField,
                status: false,
              });
            Loader.modal(false);
            if (statusResult) {
              aletMessage(false);
            }
          },
        });
      } else {
        setStatusField(!statusField);
      }
    } else if (fieldName === "message") {
      setMessageField(fieldValue);
      messageValid.current = isValid ?? false;
    }
  };

  const handleSubmit = async () => {
    if (messageValid.current && UtilityHelper.isNotEmpty(messageField)) {
      if (
        messageCurrentRef.current.toLowerCase() === messageField.toLowerCase()
      ) {
        aletMessage("same");
      } else {
        Loader.modal();
        const statusResult = await AutomatedReplyProvider.updateAutomatedAPI({
          message: messageField,
        });
        Loader.modal(false);
        if (statusResult) {
          aletMessage(true);
        }
      }
    } else {
      if (UtilityHelper.isEmpty(messageField)) {
        ShowToast.warning(
          AppTranslation.text("automated_reply_cannot_be_empty")
        );
      }

      if (UtilityHelper.countWords(messageField) > maxWords) {
        ShowToast.warning(AppTranslation.text("character_exceeded"));
      }
    }
  };

  const aletMessage = (status: boolean | "same" = true) => {
    AlertMesage({
      title:
        status || "same"
          ? "automated_reply_activated"
          : "automated_reply_deactivated",
      onClick: () => {
        if (status !== "same") {
          dispatch(removeAllModal());
          dispatch(
            addModal({ id: ModalKeys.work_profile_menu, title: "work_profile" })
          );
        }
      },
    });
  };

  const getAutomatedAPI = async () => {
    resetForm();
    setLoadedStatus(false);
    const automatedResult: AutomatedReplyModel | null =
      await AutomatedReplyProvider.getAutomatedAPI();

    if (automatedResult != null) {
      let message = automatedResult?.result?.menu_auto_reply_message!.trim();
      message = textCase(message!);

      setMessageField(message);
      setStatusField(UtilityHelper.isEmpty(message) ? false : true);

      messageValid.current = UtilityHelper.isEmpty(message) ? false : true;
      messageCurrentRef.current = message;

      setAllowEdit(UtilityHelper.isNotEmpty(message) ? false : true);
    }

    setLoadedStatus(true);
  };

  const handleEdit = () => {
    setAllowEdit(true);
  };

  const handleClear = () => {
    setMessageField("");
    messageValid.current = false;
  };

  useEffect(() => {
    getAutomatedAPI();
    // eslint-disable-next-line
  }, []);

  return (
    <AutomatedReplyContext.Provider
      value={{
        maxWords,
        statusField,
        messageField,
        loadedStatus,
        allowEdit,

        handleEdit,
        handleSubmit,
        handleOnChange,
        handleClear,
      }}
    >
      {children}
    </AutomatedReplyContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const AutomatedReplyContextImport = () => {
  const context = useContext(AutomatedReplyContext);
  if (!context) {
    throw new Error(
      "AutomatedReplyContextImport must be used within a AutomatedReplyContextProvider"
    );
  }
  return context;
};
